/**
 * 🟥 CUADRO NEGRO 2026-02 — ProjectAdaptPrompt
 *
 * Mensaje del asistente que aparece cuando el usuario manda un proyecto
 * en código. Pregunta si quiere que DamyxIA lo adapte y lo convierta en
 * un módulo del Área de Trabajo.
 *
 * Props:
 *   - offer: { rawMessage, suggestedName, language, fileCountEstimate }
 *   - onAccept(offer): Promise<bool>  // dispara /api/projects/build
 *   - onDecline():     void
 *   - status: 'idle' | 'building' | 'accepted' | 'declined'
 */
import { useState } from 'react';
import { FolderTree, Check, X, Loader2, Code2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

export const ProjectAdaptPrompt = ({
  offer,
  onAccept,
  onDecline,
  initialStatus = 'idle',
}) => {
  const { language } = useLanguage();
  const [status, setStatus] = useState(initialStatus);

  const isEs = (language || 'es').toLowerCase().startsWith('es');

  if (!offer) return null;

  const handleAccept = async () => {
    if (status === 'building' || status === 'accepted') return;
    setStatus('building');
    try {
      const ok = await onAccept?.(offer);
      setStatus(ok ? 'accepted' : 'idle');
    } catch {
      setStatus('idle');
    }
  };

  const handleDecline = () => {
    if (status === 'building' || status === 'accepted') return;
    setStatus('declined');
    onDecline?.();
  };

  return (
    <div
      className="mt-1 rounded-xl border border-emerald-500/40 bg-emerald-950/30 light:bg-emerald-50 light:border-emerald-200 p-4 sm:p-5"
      data-testid="project-adapt-prompt"
    >
      <div className="flex items-start gap-3">
        <FolderTree className="w-5 h-5 text-emerald-400 light:text-emerald-600 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-semibold text-emerald-300 light:text-emerald-800 mb-1">
            {isEs ? 'Detecté un proyecto en código' : 'I detected a code project'}
          </h4>
          <p className="text-sm text-zinc-300 light:text-zinc-700 leading-relaxed">
            {isEs
              ? 'Para poder trabajar en este proyecto debo adaptarlo a mi entorno y capacidades. ¿Deseas que lo convierta en un proyecto compatible para el Área de Trabajo?'
              : 'To work on this project I need to adapt it to my environment. Do you want me to convert it into a workspace-compatible project?'}
          </p>

          <div className="flex flex-wrap items-center gap-2 mt-2 text-xs text-zinc-400 light:text-zinc-500">
            {offer.language && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-300 light:text-emerald-700">
                <Code2 className="w-3 h-3" />
                {offer.language}
              </span>
            )}
            {offer.fileCountEstimate ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-zinc-700/40 text-zinc-300 light:bg-zinc-200 light:text-zinc-700">
                ~{offer.fileCountEstimate} {isEs ? 'archivos detectados' : 'files detected'}
              </span>
            ) : null}
            {offer.suggestedName && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-zinc-700/40 text-zinc-300 light:bg-zinc-200 light:text-zinc-700">
                &ldquo;{offer.suggestedName}&rdquo;
              </span>
            )}
          </div>

          {/* Acciones */}
          <div className="flex flex-wrap items-center gap-2 mt-4">
            <button
              onClick={handleAccept}
              disabled={status === 'building' || status === 'accepted' || status === 'declined'}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-emerald-500 hover:bg-emerald-400 text-white font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="project-adapt-accept-btn"
            >
              {status === 'building' ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  {isEs ? 'Adaptando…' : 'Adapting…'}
                </>
              ) : status === 'accepted' ? (
                <>
                  <Check className="w-3.5 h-3.5" />
                  {isEs ? 'Listo en el Área de Trabajo' : 'Ready in Workspace'}
                </>
              ) : (
                <>
                  <Check className="w-3.5 h-3.5" />
                  {isEs ? 'Sí, adaptarlo' : 'Yes, adapt it'}
                </>
              )}
            </button>
            <button
              onClick={handleDecline}
              disabled={status === 'building' || status === 'accepted' || status === 'declined'}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-zinc-700/60 hover:bg-zinc-700 text-zinc-200 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="project-adapt-decline-btn"
            >
              <X className="w-3.5 h-3.5" />
              {status === 'declined'
                ? (isEs ? 'Continuando en el chat' : 'Continuing in chat')
                : (isEs ? 'No, seguir en el chat' : 'No, keep in chat')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectAdaptPrompt;
